import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import QueryKeys from '@/utils/QueryKeys';
import type { FilterProperty, SelectOption } from '@/components/admin/SysadminSPA/shared/filterTypes';
import ALERT_MESSAGE_FILTER_PROPERTIES from './filterConfig';
import type { RawAlertMessage } from './useContentAlertMessages';

interface RawProgram {
  full_title_with_edition: string;
  id: number;
}

interface RawState {
  id: number;
  name: string;
}

type FilterSource = Pick<RawAlertMessage, 'location' | 'title'>;

const uniqueOptions = (values: (string | null)[]): SelectOption[] => (
  Array.from(new Set(values.filter((value): value is string => Boolean(value))))
    .sort((a, b) => a.localeCompare(b))
    .map(value => ({ label: value, value }))
);

// AIDEV-NOTE: Options are fetched independently of the filtered rows so the dropdowns don't shrink
// as filters are applied. Locations and titles come from the unfiltered alert messages themselves.
const useFilterOptions = (): FilterProperty[] => {
  const { data: programs = [] } = useQuery({
    queryFn: async (): Promise<RawProgram[]> => {
      const response = await Axios.get(
        Routes.plato_api_programs_path({ non_archived: true, only: ['id', 'full_title_with_edition'] }),
      );

      return response.data.data;
    },
    queryKey: [QueryKeys.ContentPrograms, 'filter-options'],
  });

  const { data: states = [] } = useQuery({
    queryFn: async (): Promise<RawState[]> => {
      const response = await Axios.get(Routes.plato_api_states_path({ only: ['id', 'name'] }));

      return response.data.data;
    },
    queryKey: [QueryKeys.States, 'filter-options'],
  });

  const { data: sources = [] } = useQuery({
    queryFn: async (): Promise<FilterSource[]> => {
      const response = await Axios.get(
        Routes.plato_api_alert_messages_path({ only: ['title', 'location'], page: 1, per_page: 10000 }),
      );

      return response.data.data;
    },
    queryKey: [QueryKeys.ContentAlertMessages, 'filter-options'],
  });

  return useMemo(() => {
    const optionsMap: Record<string, SelectOption[]> = {
      location: uniqueOptions(sources.map(source => source.location)),
      programs_id: programs
        .filter(p => p.full_title_with_edition && !p.full_title_with_edition.includes('{placeholder}'))
        .map(p => ({ label: p.full_title_with_edition, value: String(p.id) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
      states_id: states.map(state => ({ label: state.name, value: String(state.id) })),
      title: uniqueOptions(sources.map(source => source.title)),
    };

    return ALERT_MESSAGE_FILTER_PROPERTIES.map(prop => ({
      ...prop,
      options: prop.options ?? optionsMap[prop.value],
    }));
  }, [programs, sources, states]);
};

export default useFilterOptions;
